import { supabase } from './supabase';
import { postJournal, voidJournal } from './journal';
import { ACCOUNTS } from './accounts';
import { getClientDepositBalance, getInvoicePaidAmount } from './receiptAllocations';

export const CHEQUES_IN_HAND_ID = 'ebe40e7c-de6d-4f76-8ea3-dab8a9df14e6'; // 1005

// Deposit one cheque held in hand into a bank account: DR Bank / CR Cheques in Hand.
export async function depositCheque(cheque, { accountId, date, ref, username }) {
  const je = await postJournal({
    date,
    description: `Cheque deposit: ${cheque.voucher_number}${ref ? ` | Ref: ${ref}` : ''}`,
    reference_type: 'CHEQUE_DEPOSIT',
    reference_id: cheque.id,
    currency_id: cheque.currency_id,
    exchange_rate: cheque.exchange_rate || 1,
    created_by: username,
    branch_id: cheque.branch_id || null,
    lines: [
      { account_id: accountId, debit_amount: cheque.amount, credit_amount: 0 },
      { account_id: CHEQUES_IN_HAND_ID, debit_amount: 0, credit_amount: cheque.amount },
    ],
  });
  await supabase.from('receipt_vouchers')
    .update({ voucher_status: 'BANKED', bank_reference: ref || cheque.bank_reference, banked_at: date, banked_into_account_id: accountId })
    .eq('id', cheque.id);
  return je;
}

/**
 * A cheque bounced / was returned by the bank. Fully reverses everything the cheque did, so the
 * customer owes the money again exactly as if it had never been received:
 *  - every posted journal tied to this voucher (the receipt, a bank deposit if it was already
 *    banked, a suspense reclassification if it was an unidentified deposit) is reversed via
 *    voidJournal — nothing is deleted, the reversing entries are the audit trail;
 *  - receipt_allocations that credited invoices are removed (their amounts are kept on the
 *    voucher in returned_allocations) and any invoice that was PAID only because of this cheque
 *    flips back to SENT;
 *  - if the cheque's money had been parked in Client Deposits and already applied to invoices, the
 *    reversal would leave that deposit pool negative — that shortfall is moved onto the client's
 *    AR (DR AR / CR Client Deposits) so the customer shows as owing it, not as a negative deposit.
 * voided_at is set too, so every existing "ignore voided receipts" rule (legacy invoice paid
 * amounts, quotation deposits) automatically stops counting this cheque.
 */
export async function returnCheque(voucher, { reason, username }) {
  if (!['IN_HAND', 'BANKED'].includes(voucher.voucher_status)) throw new Error('Only a cheque in hand or banked can be returned.');
  return undoReceipt(voucher, {
    reason: `Cheque returned: ${reason}`, username, refType: 'CHEQUE_RETURN',
    arDescription: `Cheque returned (${voucher.voucher_number}) — deposit already applied, balance restored to AR`,
    statusFields: { voucher_status: 'RETURNED', returned_at: new Date().toISOString(), returned_by: username, return_reason: reason },
  });
}

/**
 * Void any receipt voucher (cash, transfer or cheque) — the same full undo as a returned
 * cheque, just recorded as VOID. Voiding used to reverse only the receipt's own journal, so a
 * deposit that had already been applied to an invoice (DR Client Deposits / CR AR) stayed put:
 * the invoice kept the credit with no money behind it and the deposit pool went negative
 * (RV-63974187 → JE-1299 on TLS-26-0146 was exactly this).
 */
export async function voidReceipt(voucher, { reason, username }) {
  if (['VOID', 'RETURNED'].includes(voucher.voucher_status)) throw new Error('This receipt is already voided.');
  return undoReceipt(voucher, {
    reason: reason || 'Voided by user', username, refType: 'RECEIPT_VOID',
    arDescription: `Receipt voided (${voucher.voucher_number}) — deposit already applied, balance restored to AR`,
    statusFields: { voucher_status: 'VOID' },
  });
}

async function undoReceipt(voucher, { reason, username, refType, arDescription, statusFields }) {
  // Deposit pool before reversing anything — used in step 4 to isolate the shortfall caused
  // by THIS undo from any negative balance the client already had for unrelated reasons.
  const depositBefore = voucher.client_id ? await getClientDepositBalance(voucher.client_id) : 0;

  // 1. Reverse every live journal tied to this voucher.
  const ids = new Set();
  if (voucher.journal_entry_id) ids.add(voucher.journal_entry_id);
  if (voucher.suspense_cleared_journal_id) ids.add(voucher.suspense_cleared_journal_id);
  const { data: linked } = await supabase.from('journal_entries')
    .select('id').eq('reference_id', voucher.id).is('reversal_of', null).eq('status', 'POSTED');
  (linked || []).forEach(j => ids.add(j.id));
  for (const jid of ids) await voidJournal(jid, reason, username);

  // 2. Take the receipt back out of invoice payment tracking.
  const { data: allocs } = await supabase.from('receipt_allocations')
    .select('id, invoice_id, target_type, amount').eq('receipt_voucher_id', voucher.id);
  const invoiceIds = new Set((allocs || []).filter(a => a.invoice_id).map(a => a.invoice_id));
  if (voucher.reference_type === 'invoice' && voucher.reference_id) invoiceIds.add(voucher.reference_id);
  if (allocs?.length) await supabase.from('receipt_allocations').delete().eq('receipt_voucher_id', voucher.id);

  await supabase.from('receipt_vouchers').update({
    ...statusFields,
    returned_allocations: allocs?.length ? allocs : null,
    voided_at: new Date().toISOString(),
    voided_by: username,
    void_reason: reason,
  }).eq('id', voucher.id);

  // 3. Reopen invoices that are no longer fully paid.
  const reopened = [];
  for (const invId of invoiceIds) {
    const { data: inv } = await supabase.from('invoices').select('id, invoice_number, status, line_items').eq('id', invId).single();
    if (!inv || inv.status !== 'PAID') continue;
    const total = (inv.line_items || []).reduce((s, li) =>
      s + (parseFloat(li.unit_price ?? li.unitPrice ?? li.amount ?? 0) * parseFloat(li.qty ?? li.quantity ?? 1)), 0);
    const paid = await getInvoicePaidAmount(invId);
    if (paid < total - 0.01) {
      await supabase.from('invoices').update({ status: 'SENT' }).eq('id', invId);
      reopened.push(inv.invoice_number);
    }
  }

  // 4. If the reversal drove the client's deposit pool negative, move that onto AR.
  let movedToAr = 0;
  if (voucher.client_id) {
    const depositAfter = await getClientDepositBalance(voucher.client_id);
    const shortfall = Math.max(0, -depositAfter) - Math.max(0, -depositBefore);
    if (shortfall > 0.01) {
      movedToAr = Math.min(shortfall, parseFloat(voucher.base_amount || voucher.amount) || shortfall);
      const { data: lkr } = await supabase.from('currencies').select('id').eq('code', 'LKR').single();
      const desc = arDescription;
      await postJournal({
        date: new Date().toISOString().slice(0, 10),
        description: desc,
        reference_type: refType,
        reference_id: voucher.id,
        currency_id: lkr?.id,
        exchange_rate: 1,
        created_by: username,
        branch_id: voucher.branch_id || null,
        lines: [
          { account_id: ACCOUNTS.AR, debit_amount: movedToAr, credit_amount: 0, description: desc, party_type: 'client', party_id: voucher.client_id },
          { account_id: ACCOUNTS.CLIENT_DEPOSITS, debit_amount: 0, credit_amount: movedToAr, description: desc, party_type: 'client', party_id: voucher.client_id },
        ],
      });
    }
  }

  return { reopened, movedToAr, hadClient: !!voucher.client_id };
}
